// Composer attachment plumbing for CopilotPanel's plus button.
//
// EVERY SUPPORTED TYPE REACHES THE COPILOT. The file's BYTES are uploaded to
// POST /api/tse/attachments and the returned id rides on tse:message; the server
// turns each one into text before the agent sees the turn -- md/txt read
// directly, PDFs text-extracted (or read as images when they are scans), and
// screenshots transcribed by a vision model. See server/app/tse_agent/attachments.py.
//
// This used to happen in the browser, which is why PDFs and images did not work:
// a browser can paste a .txt into the outgoing string, but it has nothing to
// paste for a PDF or a PNG, so those were attached, named, and marked unread.
//
// The transcript bubble still shows only what the engineer typed, with the file
// as a chip -- the file's content is for the model, not the reader.

import { authFetch } from "../../../utils/auth";

export const ALLOWED_EXTENSIONS = {
  pdf: "document",
  md: "document",
  txt: "document",
  // Diagnostic captures. The server reads each one differently (a log from its
  // end, a HAR as a request table, a pcap via dpkt) -- see attachments.py. Here
  // they are simply "document", because `kind` only decides which of the two
  // picker entries accepts them.
  log: "document",
  har: "document",
  pcap: "document",
  jpg: "image",
  jpeg: "image",
  png: "image",
};

// Separate `accept` lists for the menu's two entries ("Add images" / "Add
// files") -- each opens its own native picker pre-filtered to its kind, the
// way ChatGPT's tabs do, even though both funnel into the same validation.
export const ACCEPT_IMAGES = ".jpg,.jpeg,.png,image/jpeg,image/png";
// .har and .log have no reliable registered MIME type across browsers (Chrome
// reports "" for .har, and .log is text/plain on some platforms and "" on
// others), so the extensions carry the filter and the server revalidates.
export const ACCEPT_DOCUMENTS =
  ".pdf,.md,.txt,.log,.har,.pcap," +
  "application/pdf,text/plain,text/markdown,application/json";

export const MAX_ATTACHMENTS = 5;
export const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB

// Single source of truth for the "what's supported" hint shown alongside a
// rejected-file error.
export const SUPPORTED_FORMATS_LABEL = Object.keys(ALLOWED_EXTENSIONS)
  .map((ext) => ext.toUpperCase())
  .join(", ");

// Formats still proving themselves. Each needs a real extraction step rather
// than a plain read -- a log is read from its end, a HAR is summarised into a
// request table, a pcap is parsed with dpkt -- so the useful-output rate depends
// on the shape of the file in a way PDF and TXT never did. Flagged in the UI so
// an engineer knows to check what came back rather than assume.
//
// Drop an extension from here once its extraction is trusted; nothing else
// changes, the labels below follow.
export const BETA_EXTENSIONS = new Set(["log", "har", "pcap"]);

export const isBetaExt = (ext) => BETA_EXTENSIONS.has(String(ext || "").toLowerCase());

// The two picker subtitles, derived rather than written out. The menu used to
// spell "PDF, MD, TXT" inline, so adding a format to ALLOWED_EXTENSIONS silently
// left the menu advertising the old list.
const labelFor = (kind, { beta = null } = {}) =>
  Object.entries(ALLOWED_EXTENSIONS)
    .filter(([ext, k]) => k === kind && (beta === null || isBetaExt(ext) === beta))
    .map(([ext]) => ext.toUpperCase())
    .join(", ");

export const IMAGE_FORMATS_LABEL = labelFor("image");
export const DOCUMENT_FORMATS_LABEL = labelFor("document");
// Split so the menu can mark the experimental half without hardcoding which
// half that is.
export const DOCUMENT_STABLE_LABEL = labelFor("document", { beta: false });
export const DOCUMENT_BETA_LABEL = labelFor("document", { beta: true });

export const extOf = (name) => (String(name || "").split(".").pop() || "").toLowerCase();

// Extension-based, not MIME-based: browsers report inconsistent (or empty)
// MIME types for .md, so the extension is the only reliable signal.
export const kindOf = (file) => ALLOWED_EXTENSIONS[extOf(file?.name)] || null;

export const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Splits a FileList/array into accepted attachment records and rejection
 * reasons, respecting `roomLeft` (how many more the composer can hold).
 * Doesn't touch component state -- the caller merges `accepted` in and
 * surfaces `rejected` as an error string.
 */
export function triageFiles(files, roomLeft) {
  const accepted = [];
  const rejected = [];
  for (const file of Array.from(files || [])) {
    const kind = kindOf(file);
    if (!kind) {
      rejected.push(`${file.name}: unsupported file type`);
    } else if (file.size > MAX_FILE_BYTES) {
      rejected.push(`${file.name}: over ${formatBytes(MAX_FILE_BYTES)}`);
    } else if (accepted.length >= roomLeft) {
      rejected.push(`${file.name}: attach up to ${MAX_ATTACHMENTS} files`);
    } else {
      accepted.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        name: file.name,
        size: file.size,
        kind,
        // Revoked on removal (CopilotPanel.removeAttachment) and on unmount
        // -- kept alive while attached so the chip/thumbnail and, after
        // send, the transcript bubble can both render it.
        previewUrl: kind === "image" ? URL.createObjectURL(file) : null,
      });
    }
  }
  return { accepted, rejected };
}


// ---------------------------------------------------------------------------
// Sending file CONTENT to the copilot
// ---------------------------------------------------------------------------

/**
 * Uploads the attachments and returns the ids `tse:message` should carry.
 *
 * One request per file, concurrently -- a PDF and two screenshots are three
 * independent uploads and the engineer waits on the slowest, not their sum.
 *
 * A file that fails to upload is reported in `failed` and simply does not get an
 * id: the turn still goes, and the copilot is never told about a file it was not
 * given. Silently sending the message as if nothing were attached would be worse
 * -- the engineer would read an answer that ignored their evidence and not know why.
 *
 * `authFetch` sets Authorization; Content-Type is deliberately NOT set, so the
 * browser writes the multipart boundary itself.
 */
export async function uploadAttachments(sessionId, attachments) {
  const list = Array.from(attachments || []);
  if (!list.length) return { ids: [], failed: [] };

  const results = await Promise.all(list.map(async (att) => {
    const form = new FormData();
    form.append("session_id", sessionId);
    form.append("file", att.file, att.name);
    try {
      const res = await authFetch("/api/tse/attachments", { method: "POST", body: form });
      if (!res.ok) {
        let detail = `upload failed (${res.status})`;
        try {
          const body = await res.json();
          if (body?.detail) detail = String(body.detail);
        } catch { /* non-JSON error body -- the status is enough */ }
        return { error: `${att.name}: ${detail}` };
      }
      const body = await res.json();
      return { id: body?.attachment_id };
    } catch (err) {
      return { error: `${att.name}: ${err?.message || "upload failed"}` };
    }
  }));

  return {
    ids: results.map((r) => r.id).filter(Boolean),
    failed: results.map((r) => r.error).filter(Boolean),
  };
}
